import { useState } from "react";
import { Pencil, Trash2, Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { useAllShippingCredentials, useDeleteCredential } from "@/hooks/use-shipping";
import { CredentialDialog } from "./credential-dialog";
import { COMPANY_TYPES } from "@/lib/shipping/types";
import { toast } from "sonner";

export function CredentialsSection() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const { data, isLoading } = useAllShippingCredentials(search, page, 25);
  const deleteMut = useDeleteCredential();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editCredential, setEditCredential] = useState<any>(null);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);

  function handleEdit(cred: any) {
    setEditCredential(cred);
    setDialogOpen(true);
  }

  function handleAdd() {
    setEditCredential(null);
    setDialogOpen(true);
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      await deleteMut.mutateAsync(deleteTarget.id);
      toast.success("Kurirski nalog obrisan");
      setDeleteTarget(null);
    } catch (e: any) {
      toast.error(e?.message || "Greška pri brisanju naloga");
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Pretraži kurirske naloge..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="h-9 pl-9"
          />
        </div>
        <Button size="sm" onClick={handleAdd} className="h-9">
          <Plus className="h-3.5 w-3.5 mr-1" /> Dodaj
        </Button>
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
        </div>
      ) : !data?.data?.length ? (
        <p className="text-xs text-muted-foreground py-8 text-center">
          {search ? "Nema rezultata" : "Nema kurirskih naloga"}
        </p>
      ) : (
        <>
          <div className="ring-1 ring-foreground/10 overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="p-2 text-left font-medium">Naziv</th>
                  <th className="p-2 text-left font-medium">Tip</th>
                  <th className="p-2 text-left font-medium hidden sm:table-cell">Trgovina</th>
                  <th className="p-2 text-left font-medium hidden md:table-cell">Vrsta robe</th>
                  <th className="p-2 text-left font-medium">Status</th>
                  <th className="p-2 text-right font-medium">Akcije</th>
                </tr>
              </thead>
              <tbody>
                {data.data.map((cred: any) => (
                  <tr key={cred.id} className="border-b hover:bg-muted/50 transition-colors">
                    <td className="p-2">
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 shrink-0" style={{ backgroundColor: cred.color }} />
                        <span className="font-medium">{cred.name}</span>
                      </div>
                    </td>
                    <td className="p-2">
                      <Badge variant="outline">{COMPANY_TYPES[cred.companyType] || cred.companyType}</Badge>
                    </td>
                    <td className="p-2 text-muted-foreground hidden sm:table-cell">{cred.storeName || "—"}</td>
                    <td className="p-2 text-muted-foreground hidden md:table-cell">{cred.goodsType}</td>
                    <td className="p-2">
                      <Badge variant={cred.active ? "default" : "secondary"}>
                        {cred.active ? "Aktivan" : "Neaktivan"}
                      </Badge>
                    </td>
                    <td className="p-2 text-right">
                      <div className="flex gap-1 justify-end">
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(cred)}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(cred)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {data.total > 25 && (
            <div className="flex justify-center gap-2">
              <Button variant="outline" size="sm" disabled={!data.hasPreviousPage} onClick={() => setPage(page - 1)}>
                Prethodna
              </Button>
              <span className="flex items-center text-xs text-muted-foreground tabular-nums">
                {page} / {Math.ceil(data.total / 25)}
              </span>
              <Button variant="outline" size="sm" disabled={!data.hasNextPage} onClick={() => setPage(page + 1)}>
                Sljedeća
              </Button>
            </div>
          )}
        </>
      )}

      <CredentialDialog open={dialogOpen} onOpenChange={setDialogOpen} credential={editCredential} />

      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Obriši kurirski nalog</DialogTitle>
            <DialogDescription>
              Jeste li sigurni da želite obrisati "{deleteTarget?.name}"? Postojeći zapisi o pošiljkama će biti sačuvani.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Otkaži</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteMut.isPending}>Obriši</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
